<script lang="ts">
	import { onDestroy } from 'svelte';
	interface AdminPageState {
		items: unknown[];
		total: number;
		nextCursor?: string;
		loading: boolean;
		error: string | null;
		q: string;
		status: string;
		loaded: boolean;
	}

	interface StatusOption {
		value: string;
		label: string;
	}

	interface Props {
		state: AdminPageState;
		statuses?: StatusOption[];
		placeholder?: string;
		onapply: () => void;
		onloadmore: () => void;
		onretry: () => void;
	}

	let { state, statuses = [], placeholder = 'Поиск', onapply, onretry }: Props = $props();
	let timer: ReturnType<typeof setTimeout> | undefined;
	function apply(): void {
		clearTimeout(timer);
		onapply();
	}
	function search(): void {
		clearTimeout(timer);
		timer = setTimeout(apply, 300);
	}
	function reset(): void {
		state.q = '';
		state.status = '';
		apply();
	}
	onDestroy(() => clearTimeout(timer));
	$effect(() => {
		// При смене раздела отменяем отложенный поиск предыдущего списка.
		state;
		return () => clearTimeout(timer);
	});
</script>

<div class="list-tools">
	<form
		class="filters"
		onsubmit={(event) => {
			event.preventDefault();
			apply();
		}}
	>
		<label>
			<span class="sr-only">{placeholder}</span>
			<input bind:value={state.q} {placeholder} type="search" oninput={search} />
		</label>
		{#if statuses.length > 0}
			<label>
				<span class="sr-only">Статус</span>
				<select
					bind:value={state.status}
					aria-label="Статус"
					onchange={(event) => {
						state.status = event.currentTarget.value;
						apply();
					}}
				>
					<option value="">Все статусы</option>
					{#each statuses as option (option.value)}
						<option value={option.value}>{option.label}</option>
					{/each}
				</select>
			</label>
		{/if}
		{#if state.q || state.status}
			<button type="button" onclick={reset}>Сбросить фильтры</button>
		{/if}
	</form>
	<div class="result-line" aria-live="polite">
		<span>Показано {state.items.length} из {state.total}</span>
		{#if state.error && state.items.length === 0}
			<strong>{state.error}</strong><button type="button" onclick={onretry}>Повторить</button>
		{/if}
	</div>
</div>
{#if state.loading && state.items.length === 0}
	<div class="skeleton" role="status" aria-label="Загрузка списка" aria-busy="true">
		<i></i><i></i><i></i>
	</div>
{:else if state.loaded && state.items.length === 0 && !state.error}
	<div class="empty">Ничего не найдено. Измените поиск или фильтр.</div>
{/if}

<style>
	.list-tools {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-m);
		padding: var(--spacing-m) 0;
		flex-wrap: wrap;
	}
	.filters,
	.result-line {
		display: flex;
		align-items: center;
		gap: var(--spacing-s);
		flex-wrap: wrap;
	}
	input,
	select,
	button {
		min-height: var(--admin-control-height);
		border: var(--size-1) solid var(--border-subtle);
		border-radius: var(--radius-s);
		background: var(--background-secondary);
		color: var(--text-primary);
		font: inherit;
	}
	input,
	select {
		padding: 0 var(--spacing-m);
	}
	input {
		width: min(var(--admin-search-width), 62vw);
	}
	button {
		padding: 0 var(--spacing-m);
		cursor: pointer;
	}
	button:disabled {
		opacity: 0.55;
		cursor: wait;
	}
	.result-line {
		color: var(--text-secondary);
		font-size: var(--font-size-s);
	}
	.result-line strong {
		color: var(--status-danger);
		font-weight: 600;
	}
	.sr-only {
		position: absolute;
		width: var(--size-1);
		height: var(--size-1);
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border: 0;
	}
	.skeleton {
		display: grid;
		gap: var(--spacing-s);
		padding: var(--spacing-xs) 0 var(--spacing-m);
	}
	.skeleton i {
		height: var(--admin-row-height);
		border-radius: var(--radius-m);
		background: linear-gradient(
			90deg,
			var(--background-secondary),
			var(--background-tertiary),
			var(--background-secondary)
		);
		background-size: 200% 100%;
		animation: pulse 1.3s linear infinite;
	}
	.empty {
		padding: var(--spacing-2xl) var(--spacing-l);
		text-align: center;
		color: var(--text-secondary);
	}
	@keyframes pulse {
		to {
			background-position: -200% 0;
		}
	}
	@media (max-width: 900px) {
		input,
		select,
		button {
			min-height: var(--admin-touch-target);
		}
		.list-tools,
		.filters,
		.result-line {
			align-items: stretch;
			width: 100%;
		}
		.filters label:first-child,
		.filters label:first-child input {
			width: 100%;
		}
		.result-line {
			justify-content: space-between;
			align-items: center;
		}
	}
</style>
